import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore/lite';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import {
  buildReportData, DEFAULT_WEIGHTINGS, scoreInventoryFromChecks,
} from '../utils/metrics';
import '../print.css';

import PrintCover from '../components/pdf/PrintCover';
import PrintFrameworkIntro from '../components/pdf/PrintFrameworkIntro';
import PrintAwards from '../components/pdf/PrintAwards';
import PrintTopics from '../components/pdf/PrintTopics';
import {
  PrintSharingSpace, PrintAdvocacy, PrintBrevity, PrintFrequency, PrintDialogueFrequency,
} from '../components/pdf/PrintDataPages';
import PrintGenius from '../components/pdf/PrintGenius';
import PrintTeamBreakdown from '../components/pdf/PrintTeamBreakdown';
import PrintQuotes from '../components/pdf/PrintQuotes';
import PrintObservations from '../components/pdf/PrintObservations';
import PrintRoles from '../components/pdf/PrintRoles';
import PrintRolesPage2 from '../components/pdf/PrintRolesPage2';
import PrintCoverV2 from '../components/pdf/v2/PrintCoverV2';
import PrintProcessMethodology from '../components/pdf/v2/PrintProcessMethodology';
import PrintProcessTally from '../components/pdf/v2/PrintProcessTally';
import PrintTopicsV2 from '../components/pdf/v2/PrintTopicsV2';
import PrintTimelineV2 from '../components/pdf/v2/PrintTimelineV2';
import PrintTeamBreakdownV2 from '../components/pdf/v2/PrintTeamBreakdownV2';
import PrintDashboard4Up from '../components/pdf/v2/PrintDashboard4Up';
import PrintGeniusBarsAndEkg from '../components/pdf/v2/PrintGeniusBarsAndEkg';
import PrintQuotesAndMoments from '../components/pdf/v2/PrintQuotesAndMoments';
import PrintSummaryV2 from '../components/pdf/v2/PrintSummaryV2';
import PrintQuestionComparison from '../components/pdf/v2/PrintQuestionComparison';

export default function ReportPrint() {
  const { meetingId } = useParams();
  const token = new URLSearchParams(window.location.search).get('token');
  const mode = new URLSearchParams(window.location.search).get('mode') || 'full';
  const isQuick = mode === 'quick';
  const isV2 = mode === 'v2';

  const [status, setStatus] = useState('loading'); // loading | valid | error
  const [meeting, setMeeting] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [inventoryScores, setInventoryScores] = useState({});
  const [contentMap, setContentMap] = useState({});
  const [questionComparison, setQuestionComparison] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        // 1. Validate print token + fetch meeting (server-side, token-gated — see functions/index.js getMeetingForPrint)
        if (!token) { setStatus('error'); return; }
        const getMeetingForPrint = httpsCallable(functions, 'getMeetingForPrint');
        let result;
        try {
          result = await getMeetingForPrint({ token, meetingId });
        } catch (err) {
          setStatus('error'); return;
        }
        const { meeting: meetingRaw, questionComparison: qc } = result.data;
        if (!meetingRaw) { setStatus('error'); return; }
        setQuestionComparison(qc || []);

        // 2. Entries are a field on the meeting doc (not a subcollection)
        const meetingData = { id: meetingId, ...meetingRaw };
        const entries = meetingData.entries || [];

        // 4. Build report data (same call as MeetingReport.js)
        const rd = buildReportData(
          entries,
          meetingData.inventoryScores || {},
          meetingData.weightings || DEFAULT_WEIGHTINGS,
        );
        setReportData(rd);
        setMeeting(meetingData);

        // 5. Compute inventory scores for cover + observations
        // scoreInventoryFromChecks returns { groups: { simpleProcess: { pct }, compellingContent: { pct }, healthyBehaviors: { pct }, rightGenius: { pct } }, ... }
        const invResult = scoreInventoryFromChecks(meetingData.inventoryChecks || {});
        const g = invResult.groups || {};
        const spPct = g.simpleProcess?.pct ?? 0;
        const ccPct = g.compellingContent?.pct ?? 0;
        const hbPct = g.healthyBehaviors?.pct ?? 0;
        const rgPct = g.rightGenius?.pct ?? 0;
        setInventoryScores({
          sp: spPct,
          cc: ccPct,
          hb: hbPct,
          rg: rgPct,
          overall: Math.round((spPct + ccPct + hbPct + rgPct) / 4),
        });

        // 6. Fetch reportContent section text
        const contentKeys = ['sharing-space', 'advocacy-inquiry', 'brevity', 'frequency', 'dialogue-frequency', 'right-genius'];
        const contentDocs = await Promise.all(
          contentKeys.map(k => getDoc(doc(db, 'reportContent', k)))
        );
        const cm = {};
        contentKeys.forEach((k, i) => {
          if (contentDocs[i].exists()) cm[k] = contentDocs[i].data();
        });
        setContentMap(cm);

        setStatus('valid');
      } catch (err) {
        console.error('ReportPrint load error:', err);
        setStatus('error');
      }
    }
    load();
  }, [meetingId, token]);

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat, sans-serif', color: '#6b757e' }}>
        Preparing report…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat, sans-serif', color: '#922b3f' }}>
        Invalid or expired report link.
      </div>
    );
  }

  // data-print-ready signals Puppeteer to start printing
  const entries = meeting?.entries || [];

  return (
    <div id="report-print-root" className={isQuick ? 'quick' : ''} data-print-ready="true">
      {isV2 ? (
        <>
          <PrintCoverV2 meeting={meeting} />
          <PrintTimelineV2 pageNumber={2} reportData={reportData} entries={entries} meeting={meeting} />
          <PrintTopicsV2 pageNumber={3} reportData={reportData} meeting={meeting} />
          <PrintTeamBreakdownV2 pageNumber={4} reportData={reportData} meeting={meeting} />
          <PrintDashboard4Up pageNumber={5} reportData={reportData} />
          <PrintGeniusBarsAndEkg pageNumber={6} reportData={reportData} entries={entries} />
          <PrintQuotesAndMoments pageNumber={7} reportData={reportData} />
          <PrintProcessMethodology pageNumber={8} />
          <PrintProcessTally pageNumber={9} reportData={reportData} entries={entries} />
          <PrintSummaryV2 pageNumber={10} inventoryScores={inventoryScores} meeting={meeting} />
          <PrintQuestionComparison pageNumber={11} questionComparison={questionComparison} />
        </>
      ) : isQuick ? (
        <>
          <PrintObservations inventoryScores={inventoryScores} teamPerception={{ sp: 0, cc: 0, hb: 0, rg: 0 }} portrait />
          <PrintTopics reportData={reportData} meeting={meeting} />
          <PrintSharingSpace content={contentMap['sharing-space']} reportData={reportData} meeting={meeting} chartOnly />
          <PrintAdvocacy content={contentMap['advocacy-inquiry']} reportData={reportData} chartOnly />
          <PrintFrequency content={contentMap['frequency']} reportData={reportData} meeting={meeting} chartOnly />
          <PrintDialogueFrequency content={contentMap['dialogue-frequency']} entries={meeting.entries || []} chartOnly chartTitleOverride="Meeting Heart Rate" />
          <PrintGenius reportData={reportData} meeting={meeting} chartOnly />
          <PrintAwards awards={reportData.awards} portrait />
        </>
      ) : (
        <>
          <PrintCover meeting={meeting} inventoryScores={inventoryScores} />
          <PrintFrameworkIntro />
          <PrintObservations inventoryScores={inventoryScores} teamPerception={{ sp: 0, cc: 0, hb: 0, rg: 0 }} />
          <PrintTopics reportData={reportData} meeting={meeting} />
          <PrintSharingSpace content={contentMap['sharing-space']} reportData={reportData} meeting={meeting} />
          <PrintAdvocacy content={contentMap['advocacy-inquiry']} reportData={reportData} />
          <PrintBrevity content={contentMap['brevity']} reportData={reportData} />
          <PrintFrequency content={contentMap['frequency']} reportData={reportData} meeting={meeting} />
          <PrintDialogueFrequency content={contentMap['dialogue-frequency']} entries={meeting.entries || []} />
          <PrintGenius reportData={reportData} meeting={meeting} />
          <PrintTeamBreakdown reportData={reportData} />
          <PrintQuotes reportData={reportData} meeting={meeting} />
          <PrintAwards awards={reportData.awards} />
          <PrintRoles observedRoles={meeting.observedRoles} />
          <PrintRolesPage2 observedRoles={meeting.observedRoles} />
        </>
      )}
    </div>
  );
}
